import { useForm } from 'react-hook-form';

import { recoveryEmailSenderName } from '@eduzz-product-manager/validators';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { FormBlock } from '@/components/FormBlock';
import { useReviewMode } from '@/hooks/useReviewMode';
import { useZustandSync } from '@/hooks/useZustandSync';
import { upgradeSalesByPaymentType } from '@/lib/productRules';
import useProductStore, { type UpgradeFeatureType } from '@/stores/productStore';

import Rvp from './Rvp';
import UpgradeFeatureItem from './UpgradeFeatureItem';
import ReviewUpgradeSales from '../ReviewMode/ReviewUpgradeSales';

const productUpgradesSchema = z
  .object({
    recoverySales: z.boolean(),
    recoveryEmailSenderName: recoveryEmailSenderName.or(z.literal('')),
    multipleCards: z.boolean(),
    combinedPayment: z.boolean(),
    unlimitedInstallments: z.boolean(),
    installmentsWithoutInterest: z.boolean()
  })
  .superRefine((data, ctx) => {
    if (data.recoverySales && !data.recoveryEmailSenderName) {
      ctx.addIssue({
        code: 'custom',
        path: ['recoveryEmailSenderName'],
        message: 'Nome do remetente é obrigatório.'
      });
    }
  });

type ProductUpgradesFormValues = z.infer<typeof productUpgradesSchema>;

export default function UpgradeSales() {
  const { productConfig, paymentType } = useProductStore(p => ({
    productConfig: p.upgradeSales,
    paymentType: p.payment.type
  }));

  const reviewMode = useReviewMode();

  const form = useForm<ProductUpgradesFormValues>({
    resolver: zodResolver(productUpgradesSchema),
    mode: 'onChange',
    defaultValues: {
      recoverySales: productConfig.some?.(item => item.feature === 'sales_recovery') || false,
      recoveryEmailSenderName:
        productConfig.find?.(item => item.feature === 'sales_recovery')?.recoveryEmailSenderName ?? '',
      multipleCards: productConfig.some?.(item => item.feature === 'multiple_cards') || false,
      combinedPayment: productConfig.some?.(item => item.feature === 'combined_payment') || false,
      unlimitedInstallments: productConfig.some?.(item => item.feature === 'installments_without_limitation') || false,
      installmentsWithoutInterest:
        productConfig.some?.(item => item.feature === 'installments_without_interest') || false
    }
  });

  useZustandSync<ProductUpgradesFormValues>(form.control, data => ({
    upgradeSales: [
      ...(data.recoverySales
        ? [{ feature: 'sales_recovery', recoveryEmailSenderName: data.recoveryEmailSenderName }]
        : []),
      ...(data.multipleCards ? [{ feature: 'multiple_cards' }] : []),
      ...(data.combinedPayment ? [{ feature: 'combined_payment' }] : []),
      ...(data.unlimitedInstallments ? [{ feature: 'installments_without_limitation' }] : []),
      ...(data.installmentsWithoutInterest ? [{ feature: 'installments_without_interest' }] : [])
    ] as UpgradeFeatureType[]
  }));

  const countSelected = Object.values(form.getValues()).filter(value => value).length;

  const componentsByFeature = {
    sales_recovery: <Rvp />,
    multiple_cards: (
      <UpgradeFeatureItem
        title='Múltiplos cartões'
        description='Permite que o comprador divida o valor da compra em dois ou mais cartões.'
        conversionRate='Até 30,2% a mais de conversão'
        toggle={{ id: 'toggle-multiple-cards', name: 'multipleCards' }}
      />
    ),
    combined_payment: (
      <UpgradeFeatureItem
        title='Combinar Cartão + Pix'
        description='Permite que o cliente pague uma parte via Pix e parcele o restante no cartão.'
        conversionRate='Até 18% a mais de conversão'
        toggle={{ id: 'toggle-combined-payment', name: 'combinedPayment' }}
      />
    ),
    installments_without_limitation: (
      <UpgradeFeatureItem
        title='Parcelamento sem limites'
        description='Permite parcelar a compra mesmo que o cliente não tenha limite disponível no cartão.'
        conversionRate='Até 14,2% a mais de conversão'
        toggle={{ id: 'toggle-unlimited-installments', name: 'unlimitedInstallments' }}
      />
    ),
    installments_without_interest: (
      <UpgradeFeatureItem
        title='Parcelamento sem juros'
        description='Seu cliente paga sem juros, e você assume essa diferença.'
        conversionRate='Até 22,1% a mais de conversão'
        toggle={{ id: 'toggle-interest-free-installments', name: 'installmentsWithoutInterest' }}
      />
    )
  } as Record<UpgradeFeatureType['feature'], React.ReactNode>;

  if (!upgradeSalesByPaymentType[paymentType]?.length) {
    return null;
  }

  return (
    <>
      <FormBlock
        name='upgrade-sales'
        subtitle={reviewMode ? undefined : `${countSelected} selecionados`}
        form={form}
        title='Ferramentas de conversão'
        preview={<ReviewUpgradeSales />}
      >
        <div className='flex flex-col divide-y divide-black/6'>
          <div className='flex flex-col gap-4 py-4'>
            <span className='text-content-title text-sm'>
              Aproveite essas ferramentas para melhorar seus resultados de vendas.
            </span>
          </div>

          {upgradeSalesByPaymentType[paymentType]?.map(upgrade => componentsByFeature[upgrade.feature])}
        </div>
      </FormBlock>
    </>
  );
}
